import { get, send } from "../api.js";

/** One of the signed-in tutor's own current links to a pupil. */
export interface MyTeachingLink {
  instrument: string | null;
  serviceName: string | null;
  startsAt: string;
  endsAt: string | null;
}

/** What asking to be linked from lesson history did. */
export interface TeachingLinkRequestResult {
  linked: string[];
  alreadyLinked: string[];
  needsOwnerReview: { instrument: string | null; reason: string }[];
}

/** Read-only: which instruments the signed-in tutor is linked to for this pupil. */
export function getMyTeachingLinks(studentId: string): Promise<{ links: MyTeachingLink[] }> {
  return get<{ links: MyTeachingLink[] }>(
    `/admin/students/${encodeURIComponent(studentId)}/my-teaching-links`,
  );
}

/**
 * Apply the owner-approved lesson-history rule to this one pupil for the
 * signed-in tutor. Anything the rule cannot decide is sent to the owners;
 * nothing is booked or charged.
 */
export function requestTeachingLinkFromHistory(studentId: string): Promise<TeachingLinkRequestResult> {
  return send<TeachingLinkRequestResult>(
    "POST",
    `/admin/students/${encodeURIComponent(studentId)}/teaching-links/from-history`,
    {},
  );
}

/** The one sentence a tutor needs after asking. */
export function describeTeachingLinkRequest(result: TeachingLinkRequestResult): {
  variant: "success" | "attention";
  title: string;
  details: string[];
} {
  if (result.linked.length > 0) {
    return {
      variant: "success",
      title: "You are now linked to this pupil",
      details: [
        `Linked from your confirmed lessons: ${result.linked.join(", ")}.`,
        ...result.needsOwnerReview.map((item) => item.reason),
        "No lesson was booked by this action.",
      ],
    };
  }
  if (result.needsOwnerReview.length > 0) {
    return {
      variant: "attention",
      title: "An owner has been asked to link you",
      details: [...new Set(result.needsOwnerReview.map((item) => item.reason))],
    };
  }
  return {
    variant: "success",
    title: "You are already linked to this pupil",
    details: [result.alreadyLinked.join(", ")],
  };
}
